import { Controller } from '@hotwired/stimulus'

const TYPES = { active: 'Active', ending: 'Ending soon', scheduled: 'Scheduled', expired: 'Expired' }
const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[char]))
const money = value => new Intl.NumberFormat('en-CA', {style:'currency', currency:'CAD', maximumFractionDigits:2, minimumFractionDigits:Number.isInteger(Number(value)) ? 0 : 2}).format(Number(value))
const number = value => Number(value).toLocaleString('en-CA')
const date = value => value ? new Date(`${value.slice(0,10)}T12:00:00Z`).toLocaleDateString('en-CA', {month:'short', day:'numeric', year:'numeric', timeZone:'UTC'}) : 'Date unavailable'
const badge = status => `<span class="pc-status pc-status--${escape(status)}">${TYPES[status] || 'Unknown'}</span>`

export default class extends Controller {
  static values = { base: String }
  static targets = ['stats','notice','table','pagination','categoryFilter','search','dialog','form','title','summary','createFields','dateNote','redemptions','drawerNotice','endConfirm','endButton','endShortcuts','duplicateButton','saveButton','toast']

  connect() {
    this.activeTab = 'live'
    this.page = 1
    this.connected = true
    this.today = this.element.dataset.today || new Date().toISOString().slice(0,10)
    this.load()
  }

  disconnect() {
    this.connected = false
    this.listRequest?.abort()
    this.detailRequest?.abort()
    clearTimeout(this.searchTimer)
    clearTimeout(this.toastTimer)
    this.dialogTarget.close()
  }

  beforeCache() { this.dialogTarget.close(); this.toastTarget.hidden = true }

  async request(path, options = {}) {
    const response = await fetch(`${this.baseValue}${path}`, {
      credentials:'same-origin', cache:'no-store', ...options,
      headers:{Accept:'application/json', 'Content-Type':'application/json', 'X-CSRF-Token':document.querySelector('meta[name="csrf-token"]')?.content || '', ...options.headers}
    })
    if (response.redirected || !response.headers.get('content-type')?.includes('application/json')) throw new Error('Your session may have expired. Refresh and sign in before continuing.')
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Could not complete the request. Please try again.')
    return data
  }

  async load() {
    this.listRequest?.abort()
    const request = this.listRequest = new AbortController()
    this.tableTarget.setAttribute('aria-busy', 'true')
    this.noticeTarget.hidden = true
    try {
      const params = new URLSearchParams({tab:this.activeTab, category:this.categoryFilterTarget.value, q:this.searchTarget.value, page:this.page})
      const data = await this.request(`/data?${params}`, {signal:request.signal})
      if (!this.connected || request.signal.aborted) return
      this.today = data.today
      this.timezone = data.timezone
      this.page = data.page
      this.element.querySelectorAll('[data-count]').forEach(span => { span.textContent = number(data.counts[span.dataset.count]) })
      this.statsTarget.innerHTML = [['sell','Live codes',data.stats.live], ['confirmation_number',`Redemptions · ${data.stats.year}`,data.stats.redemptions], ['savings',`Est. value redeemed · ${data.stats.year}`,money(data.stats.value)]].map(([icon,label,value]) => `<div class="pc-stat"><span class="material-symbols-rounded" aria-hidden="true">${icon}</span><div><small>${label}</small><strong>${typeof value === 'number' ? number(value) : escape(value)}</strong></div></div>`).join('')
      this.tableTarget.innerHTML = data.rows.length ? `<div class="pc-table-wrap"><table class="pc-table"><thead><tr><th>Code</th><th class="pc-number">Value</th><th>Valid</th><th>Used for</th><th class="pc-number">Uses</th><th>Status</th></tr></thead><tbody>${data.rows.map(row => this.row(row)).join('')}</tbody></table></div>` : '<div class="pc-empty">No promo codes match these filters.</div>'
      this.paginationTarget.innerHTML = `<span>${number(data.count)} code${data.count === 1 ? '' : 's'} · ${data.count ? (data.page-1)*25+1 : 0}–${Math.min(data.page*25,data.count)} shown</span><div><button type="button" class="pc-button pc-secondary" data-page="${data.page-1}" data-action="admin-promo-codes#paginate" ${data.page===1 ? 'disabled' : ''}>Previous</button><span>${data.page} / ${data.pages}</span><button type="button" class="pc-button pc-secondary" data-page="${data.page+1}" data-action="admin-promo-codes#paginate" ${data.page===data.pages ? 'disabled' : ''}>Next</button></div>`
    } catch (error) {
      if (error.name !== 'AbortError' && this.connected) {
        this.showError(this.noticeTarget, error)
        this.tableTarget.innerHTML = '<div class="pc-empty">Promo codes could not be loaded. <button class="pc-button pc-secondary" type="button" data-action="admin-promo-codes#filter">Try again</button></div>'
        this.paginationTarget.innerHTML = ''
      }
    } finally { if (this.connected && request === this.listRequest) this.tableTarget.removeAttribute('aria-busy') }
  }

  row(row) {
    return `<tr class="${row.status==='expired' ? 'pc-expired' : ''}" data-id="${row.id}" data-action="click->admin-promo-codes#openRow"><td><div class="pc-code-cell"><button type="button" class="pc-code-link" title="${escape(row.code)}" aria-label="Edit ${escape(row.code)}">${escape(row.code)}</button>${this.copyButton(row.code)}</div></td><td class="pc-number">${money(row.amount)}</td><td>${date(row.start)} – ${date(row.end)}<small>${row.status==='scheduled' ? 'Starts' : row.status==='expired' ? 'Ended' : 'Ends'} ${date(row.status==='scheduled' ? row.start : row.end)}</small></td><td><div class="pc-note" title="${escape(row.notes)}">${escape(row.notes || 'No notes yet')}</div><span class="pc-type-label">${escape(row.type_label)}</span></td><td class="pc-number">${number(row.uses)}</td><td>${badge(row.status)}</td></tr>`
  }

  copyButton(code) { return `<button type="button" class="pc-icon-button pc-copy" data-code="${escape(code)}" data-action="admin-promo-codes#copy" aria-label="Copy ${escape(code)}"><span class="material-symbols-rounded" aria-hidden="true">content_copy</span></button>` }
  tab(event) { this.activeTab=event.currentTarget.dataset.tab; this.element.querySelectorAll('[data-tab]').forEach(button => button.setAttribute('aria-pressed',button.dataset.tab===this.activeTab)); this.filter() }
  filter(event) { event?.preventDefault(); clearTimeout(this.searchTimer); this.page=1; this.load() }
  search() { clearTimeout(this.searchTimer); this.searchTimer=setTimeout(() => this.filter(),300) }
  paginate(event) { this.page=Number(event.currentTarget.dataset.page); this.load() }
  field(name) { return this.formTarget.elements.namedItem(name) }

  async openRow(event) {
    if (this.busy) return
    this.detailRequest?.abort()
    const request = this.detailRequest = new AbortController()
    try {
      const promo = await this.request(`/${event.currentTarget.dataset.id}`, {signal:request.signal})
      if (!this.connected || request.signal.aborted) return
      this.open(promo)
    } catch(error) { if (error.name !== 'AbortError' && this.connected) this.showError(this.noticeTarget,error) }
  }

  newCode() { if (!this.busy) { this.detailRequest?.abort(); this.open(); this.generate() } }

  open(promo = null, seed = null) {
    this.promo = promo
    this.formTarget.reset()
    this.drawerNoticeTarget.hidden = true
    this.endConfirmTarget.hidden = true
    this.summaryTarget.hidden = !promo
    this.redemptionsTarget.hidden = !promo
    this.createFieldsTarget.hidden = !!promo
    this.endButtonTarget.hidden = !promo || promo.status === 'expired' || promo.automatic
    this.endShortcutsTarget.hidden = !!promo?.automatic
    this.field('end').disabled = !!promo?.automatic
    this.duplicateButtonTarget.hidden = !promo
    this.titleTarget.textContent = promo ? promo.code : 'New promo code'
    this.saveButtonTarget.textContent = promo ? 'Save changes' : 'Create code'
    for (const name of ['code','amount','start']) this.field(name).disabled = !!promo
    this.field('code').value = promo?.code || ''
    this.field('amount').value = promo?.amount || seed?.amount || 10
    this.field('start').value = promo?.start || this.today
    this.field('end').value = promo?.end || this.offsetDate(365)
    this.field('category').value = promo?.category || seed?.category || 'campaign'
    this.field('notes').value = promo?.notes || seed?.notes || ''
    this.dateNoteTarget.textContent = `Dates use ${this.timezone || 'the application timezone'}. New end dates include the whole day.${promo ? ' Code, value and start date stay unchanged.' : ''}`
    if (promo?.automatic) this.dateNoteTarget.textContent = 'Internal automatic bonus: these dates only govern manually entered codes, not the separate automatic workflow. Only type and notes can be changed here.'
    this.amountChanged()
    if (promo) {
      const progress = Math.max(0,Math.min(100,(Date.parse(this.today)-Date.parse(promo.start))/Math.max(86400000,Date.parse(promo.end)-Date.parse(promo.start))*100))
      this.summaryTarget.innerHTML = `<div class="pc-summary"><div class="pc-summary-top"><span class="pc-summary-code">${escape(promo.code)} ${this.copyButton(promo.code)}</span>${badge(promo.status)}</div><div class="pc-summary-dates"><span>${date(promo.start)}</span><span>${date(promo.end)}</span></div><progress max="100" value="${progress}" aria-label="Validity period elapsed"></progress><div class="pc-summary-kpis"><div><small>Value</small><strong>${money(promo.amount)}</strong></div><div><small>Uses</small><strong>${number(promo.uses)}</strong></div><div><small>Est. redeemed</small><strong>${money(promo.value_redeemed)}</strong></div></div></div>`
      this.redemptionsTarget.innerHTML = `<h3>Recent redemptions</h3>${promo.redemptions.length ? promo.redemptions.map(party => `<a href="/admin/parties/${party.id}"><div><strong>${escape(party.title || `Party #${party.id}`)}</strong><small>${escape(party.host || 'Host unavailable')} · #${party.id}</small></div><small>${date(party.date)}</small></a>`).join('') : '<p>No redemptions recorded yet.</p>'}`
    }
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
    if (promo) this.field(promo.automatic ? 'notes' : 'end').focus(); else this.field('code').focus()
    this.dialogTarget.querySelector('.pc-drawer-body').scrollTop = 0
  }

  close(event) { event?.preventDefault(); if (!this.busy) { this.drawerVersion=(this.drawerVersion || 0)+1; this.dialogTarget.close() } }
  backdrop(event) { if (event.target===this.dialogTarget) { const box=this.dialogTarget.getBoundingClientRect(); if (event.clientX<box.left || event.clientX>box.right) this.close(event) } }
  amount(event) { this.field('amount').value=event.currentTarget.dataset.amount; this.amountChanged() }
  amountChanged() { this.element.querySelectorAll('[data-amount]').forEach(button => button.setAttribute('aria-pressed', Number(button.dataset.amount)===Number(this.field('amount').value))) }
  offsetDate(days) { const value=new Date(`${this.today}T12:00:00Z`); if (days==='year') value.setUTCMonth(11,31); else value.setUTCDate(value.getUTCDate()+Number(days)); return value.toISOString().slice(0,10) }
  endDate(event) { this.field('end').value=this.offsetDate(event.currentTarget.dataset.days) }

  async generate() {
    if (this.busy || this.promo) return
    const version = this.drawerVersion = (this.drawerVersion || 0)+1
    const previous = this.field('code').value
    try {
      const data=await this.request('/generate',{method:'POST'})
      if (this.connected && !this.busy && !this.promo && version===this.drawerVersion && this.dialogTarget.open && this.field('code').value===previous) this.field('code').value=data.code
    } catch(error) { if (this.connected && version===this.drawerVersion && this.dialogTarget.open) this.showError(this.drawerNoticeTarget,error) }
  }

  duplicate() { if (!this.busy) { const seed=this.promo; this.open(null,seed); this.generate() } }
  endNow() { this.endConfirmTarget.hidden=false; this.endConfirmTarget.scrollIntoView({block:'nearest'}); this.endConfirmTarget.querySelector('button').focus() }
  cancelEnd() { this.endConfirmTarget.hidden=true; this.endButtonTarget.focus() }
  confirmEnd() { this.write(`/${this.promo.id}/end_now`, 'POST', {revision:this.promo.revision}, 'Promo code ended. Past contributions are unchanged.') }

  save(event) {
    event.preventDefault()
    if (this.busy || !this.formTarget.reportValidity()) return
    const promo=Object.fromEntries(new FormData(this.formTarget))
    if (this.promo) { promo.revision=this.promo.revision; if (this.promo.automatic) promo.end=this.promo.end }
    this.write(this.promo ? `/${this.promo.id}` : '', this.promo ? 'PATCH' : 'POST', {promo}, this.promo ? 'Promo code updated.' : 'Promo code created.')
  }

  async write(path, method, body, message) {
    if (this.busy) return
    this.busy = true
    this.drawerNoticeTarget.hidden = true
    const buttons=[...this.dialogTarget.querySelectorAll('button')]
    buttons.forEach(button => { button.disabled=true })
    this.saveButtonTarget.textContent='Saving…'
    try {
      await this.request(path, {method,body:JSON.stringify(body)})
      if (!this.connected) return
      this.dialogTarget.close()
      this.toast(message)
      await this.load()
    } catch(error) { if (this.connected) this.showError(this.drawerNoticeTarget,error instanceof TypeError ? new Error('Connection interrupted. Reopen this code or search for it before retrying; your change may have been saved.') : error) }
    finally {
      this.busy=false
      if (this.connected) { buttons.forEach(button => { button.disabled=false }); this.saveButtonTarget.textContent=this.promo ? 'Save changes' : 'Create code' }
    }
  }

  showError(target,error) { target.textContent=error.message; target.hidden=false; if (target===this.drawerNoticeTarget) target.scrollIntoView({block:'nearest'}) }
  toast(message) { clearTimeout(this.toastTimer); this.toastTarget.textContent=message; this.toastTarget.hidden=false; this.toastTimer=setTimeout(() => { this.toastTarget.hidden=true },4500) }
  async copy(event) {
    event.stopPropagation()
    const button=event.currentTarget
    try {
      await navigator.clipboard.writeText(button.dataset.code)
      // A native modal sits above page-level toasts, so confirm on the button too.
      button.querySelector('span').textContent='check'
      button.title='Copied'
      this.toast('Code copied.')
    } catch {
      const target=this.dialogTarget.open ? this.drawerNoticeTarget : this.noticeTarget
      this.showError(target,new Error('Copy is unavailable in this browser. Select and copy the code manually.'))
    }
  }
}
